import {
  addDays,
  isSameTimelineDay,
  isSameTimelineDateTime,
  parseTimelineDate,
  parseTimelineDateTime,
  TimelineDatedItem,
} from "./timeline-date-utils";
import {
  addTimelineTimeSteps,
  getTimelineVisualDateTime,
  isTimelineTimeZoom,
  type TimelineTimeScale,
  type TimelineZoom,
} from "./timeline-range";

export type TimelineInteractionMode =
  | "move"
  | "resize-start"
  | "resize-end"
  | "create";

export interface TimelineDateChange {
  itemId: string;
  itemLabel: string;
  mode: TimelineInteractionMode;
  previousStartDate: Date | null;
  previousDueDate: Date | null;
  nextStartDate: Date | null;
  nextDueDate: Date | null;
}

export interface TimelineDatePreview {
  previousStartDate: Date | null;
  previousDueDate: Date | null;
  nextStartDate: Date | null;
  nextDueDate: Date | null;
}

export interface TimelineEditableItem extends TimelineDatedItem {
  id: string;
  label: string;
}

const clampStartBeforeDue = (start: Date, due: Date): Date =>
  start > due ? due : start;

const clampDueAfterStart = (due: Date, start: Date): Date =>
  due < start ? start : due;

const addTimelineStepDelta = (
  date: Date,
  stepDelta: number,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): Date => {
  if (!isTimelineTimeZoom(zoom)) {
    return addDays(date, stepDelta);
  }

  return addTimelineTimeSteps(date, stepDelta, zoom, timeScale);
};

const parseTimelineInteractionDate = (
  value: TimelineDatedItem["startDate"],
  edge: "start" | "end",
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): Date | null => {
  if (!isTimelineTimeZoom(zoom)) {
    return parseTimelineDate(value);
  }

  const date = parseTimelineDateTime(value);

  return date ? getTimelineVisualDateTime(date, edge, timeScale) : null;
};

const isSameTimelineInteractionDate = (
  left: Date | null,
  right: Date | null,
  zoom: TimelineZoom,
): boolean =>
  isTimelineTimeZoom(zoom)
    ? isSameTimelineDateTime(left, right)
    : isSameTimelineDay(left, right);

export const computeTimelineDatePreview = (
  item: TimelineEditableItem,
  mode: TimelineInteractionMode,
  stepDelta: number,
  zoom: TimelineZoom = "day",
  timeScale?: TimelineTimeScale,
): TimelineDatePreview | null => {
  const previousStartDate = parseTimelineInteractionDate(
    item.startDate,
    "start",
    zoom,
    timeScale,
  );
  const previousDueDate = parseTimelineInteractionDate(
    item.dueDate,
    "end",
    zoom,
    timeScale,
  );

  if (!previousStartDate && !previousDueDate) {
    return null;
  }

  let nextStartDate = previousStartDate;
  let nextDueDate = previousDueDate;

  if (mode === "move") {
    nextStartDate = previousStartDate
      ? addTimelineStepDelta(previousStartDate, stepDelta, zoom, timeScale)
      : null;
    nextDueDate = previousDueDate
      ? addTimelineStepDelta(previousDueDate, stepDelta, zoom, timeScale)
      : null;
  }

  if (mode === "resize-start") {
    if (previousStartDate && previousDueDate) {
      nextStartDate = clampStartBeforeDue(
        addTimelineStepDelta(previousStartDate, stepDelta, zoom, timeScale),
        previousDueDate,
      );
    } else if (previousStartDate) {
      nextStartDate = addTimelineStepDelta(
        previousStartDate,
        stepDelta,
        zoom,
        timeScale,
      );
    } else if (previousDueDate) {
      nextStartDate = clampStartBeforeDue(
        addTimelineStepDelta(previousDueDate, stepDelta, zoom, timeScale),
        previousDueDate,
      );
      nextDueDate = previousDueDate;
    }
  }

  if (mode === "resize-end") {
    if (previousStartDate && previousDueDate) {
      nextDueDate = clampDueAfterStart(
        addTimelineStepDelta(previousDueDate, stepDelta, zoom, timeScale),
        previousStartDate,
      );
    } else if (previousDueDate) {
      nextDueDate = addTimelineStepDelta(
        previousDueDate,
        stepDelta,
        zoom,
        timeScale,
      );
    } else if (previousStartDate) {
      nextDueDate = clampDueAfterStart(
        addTimelineStepDelta(previousStartDate, stepDelta, zoom, timeScale),
        previousStartDate,
      );
      nextStartDate = previousStartDate;
    }
  }

  return {
    previousStartDate,
    previousDueDate,
    nextStartDate,
    nextDueDate,
  };
};

export const computeTimelineDateChange = (
  item: TimelineEditableItem,
  mode: TimelineInteractionMode,
  stepDelta: number,
  zoom: TimelineZoom = "day",
  timeScale?: TimelineTimeScale,
): TimelineDateChange | null => {
  if (stepDelta === 0) {
    return null;
  }

  const preview = computeTimelineDatePreview(
    item,
    mode,
    stepDelta,
    zoom,
    timeScale,
  );

  if (!preview) {
    return null;
  }

  if (
    isSameTimelineInteractionDate(
      preview.previousStartDate,
      preview.nextStartDate,
      zoom,
    ) &&
    isSameTimelineInteractionDate(
      preview.previousDueDate,
      preview.nextDueDate,
      zoom,
    )
  ) {
    return null;
  }

  return {
    itemId: item.id,
    itemLabel: item.label || item.id,
    mode,
    previousStartDate: preview.previousStartDate,
    previousDueDate: preview.previousDueDate,
    nextStartDate: preview.nextStartDate,
    nextDueDate: preview.nextDueDate,
  };
};

export const createTimelineDateChange = (
  item: TimelineEditableItem,
  nextStartDate: Date,
  nextDueDate: Date,
): TimelineDateChange => ({
  itemId: item.id,
  itemLabel: item.label || item.id,
  mode: "create",
  previousStartDate: parseTimelineDate(item.startDate),
  previousDueDate: parseTimelineDate(item.dueDate),
  nextStartDate,
  nextDueDate,
});
